import React, { useEffect, useState } from "react";
import Switch from "@mui/joy/Switch";
import toast from "react-hot-toast";

import AddButton from "../../../../components/admin/usable/AddButton";
import CancelButton from "../../../../components/admin/usable/CancelButton";
import EditButton from "../../../../components/admin/usable/actions/EditButton";
import DeleteButton from "../../../../components/admin/usable/actions/DeleteButton";
import TableLoader from "../../../../components/admin/usable/TableLoader";

import {
  viewDivision,
  addDivision,
  updateDivision,
  deleteDivision,
} from "../../../../api/admin/divisionApi";

import { viewCountry } from "../../../../api/admin/countryApi";

const Division = () => {
  const [DivisionData, setDivisionData] = useState([]);
  const [CountryData, setCountryData] = useState([]);
  const [Loading, setLoading] = useState(true);
  const [Submitting, setSubmitting] = useState(false);

  const [FormData, setFormData] = useState({
    id: null,
    divisionName: "",
    countryId: "",
    status: 1,
  });

  useEffect(() => {
    viewDivision(setDivisionData, setLoading);
    viewCountry(setCountryData);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleStatusChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked ? 1 : 0,
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      divisionName: "",
      countryId: "",
      status: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FormData.divisionName.trim()) {
      toast.error("Division name is required");
      return;
    }

    if (!FormData.countryId) {
      toast.error("Please select a country");
      return;
    }

    const payload = {
      ...FormData,
      divisionName: FormData.divisionName.trim(),
      countryId: Number(FormData.countryId),
      status: Number(FormData.status),
    };

    setSubmitting(true);

    try {
      if (FormData.id) {
        await updateDivision(payload, setFormData, setDivisionData, setLoading);
      } else {
        await addDivision(payload, setFormData, setDivisionData, setLoading);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      divisionName: item.divisionName || "",
      countryId: item.countryId ?? "",
      status: Number(item.status),
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this division?")) {
      deleteDivision(id, setDivisionData, setLoading);
    }
  };

  return (
    <div className="w-full bg-white border rounded-lg p-4">
      <div className="py-3">
        <p className="font-bold">Division</p>
      </div>
      <form onSubmit={handleSubmit} className="">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="divisionName" className="block mb-2">
              Division Name
            </label>

            <input
              type="text"
              id="divisionName"
              value={FormData.divisionName}
              onChange={handleChange}
              placeholder="Enter division name"
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="countryId" className="block mb-2">
              Country
            </label>

            <select
              id="countryId"
              value={FormData.countryId}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            >
              <option value="">Select Country</option>

              {CountryData.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.countryName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2">Status</label>

            <Switch
              checked={Number(FormData.status) === 1}
              onChange={handleStatusChange}
              slotProps={{
                input: { "aria-label": "Division status" },
              }}
            />
            <span className="ml-2">
              {Number(FormData.status) === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <AddButton
            type="submit"
            disabled={Submitting}
            text={Submitting ? "Submitting..." : FormData.id ? "Update" : "Add"}
          />

          <CancelButton onClick={resetForm} text={"Reset"} />
        </div>
      </form>

      <div className="overflow-x-auto pt-5">
        <table className="w-full border-collapse border">
          <thead>
            <tr>
              <th className="border p-2 text-center">SL</th>
              <th className="border p-2 text-center">Division Name</th>
              <th className="border p-2 text-center">Country</th>
              <th className="border p-2 text-center">Status</th>
              <th className="border p-2 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {Loading ? (
              <TableLoader colSpan={5} />
            ) : DivisionData.length > 0 ? (
              DivisionData.map((item, index) => (
                <tr key={item.id}>
                  <td className="border p-2 text-center">{index + 1}</td>
                  <td className="border p-2 text-center">
                    {item.divisionName}
                  </td>

                  <td className="border p-2 text-center">
                    {item.countryName ||
                      CountryData.find(
                        (country) =>
                          Number(country.id) === Number(item.countryId),
                      )?.countryName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    <span
                      className={`px-2 py-1 ${
                        Number(item.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      } rounded text-xs`}
                    >
                      {Number(item.status) === 1 ? "active" : "inactive"}
                    </span>
                  </td>

                  <td className="border p-2 text-center">
                    <div className="flex gap-2">
                      <EditButton onClick={() => handleEdit(item)} />
                      <DeleteButton onClick={() => handleDelete(item.id)} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center p-4">
                  No divisions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Division;
